import React, {
  ComponentType,
  useContext,
  useEffect
} from 'react';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import { flowRight } from 'lodash';
import { useLocation } from 'react-router';
import { ELSURLHelper } from '@els/els-ui-common-react';
import {
  RecommendationAttemptDto,
  RecommendationDto,
  RecommendationFeedbackDto
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { AssessmentSubmissionDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import {
  fetchRecommendationFeedbackAction,
  submitReviewAttemptAction
} from '../../components/app/app-state-actions.utilities';
import { AppContext } from '../../components/app/AppProvider.component';
import { RoutePath } from '../../components/app/app.constants';
import {
  getRecommendationAttemptById,
  getRecommendationById
} from '../../utilities/remediation.utilities';

type ComponentProps = ELSPropsFromToastService;

export type RecommendationPlayerHandlers = {
  handleSubmitAttempt: (timeInSeconds: number) => Promise<RecommendationAttemptDto>;
}

export type RecommendationPlayerProps = RecommendationPlayerHandlers &
  {
    recommendation: RecommendationDto;
    recommendationAttempt: RecommendationAttemptDto;
    recommendationFeedback: RecommendationFeedbackDto;
    // This would be ref like in outPostBody, but React confuses it for ref attribute
    referer: RoutePath;
  };

export type RecommendationPlayerState = {
  recommendationFeedback: RecommendationFeedbackDto;
}

const defaultState: RecommendationPlayerState = {
  recommendationFeedback: null
};

const withRecommendationPlayer = (WrappedComponent: ComponentType<RecommendationPlayerProps & ComponentProps>): ComponentType<ComponentProps> => {

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const RecommendationPlayer = (props: ComponentProps) => {
    const [state, setState] = React.useState<RecommendationPlayerState>(defaultState);

    const {
      appState,
      setAppState,
      appStateRef
    } = useContext(AppContext);
    const location = useLocation();
    const referer = ELSURLHelper.getParameterByName('ref', location.search);

    const recommendation = getRecommendationById(appStateRef, ELSURLHelper.getParameterByName('recommendationId', location.search));
    const recommendationAttempt = getRecommendationAttemptById(appStateRef, ELSURLHelper.getParameterByName('recommendationAttemptId', location.search));

    useEffect(() => {
      if (!recommendation) return;
      fetchRecommendationFeedbackAction({
        appState,
        setAppState,
        appStateRef,
        recommendationId: recommendation.id
      }).then((response) => {
        setState(() => ({
          recommendationFeedback: response
        }));
      }).catch(() => null);
    }, [recommendation?.id]);

    const handleSubmitAttempt: RecommendationPlayerHandlers['handleSubmitAttempt'] = (secondsCompleted): Promise<RecommendationAttemptDto> => {

      if (!recommendationAttempt) {
        return Promise.resolve(null);
      }

      const submission: Partial<AssessmentSubmissionDto> = {
        assessmentId: recommendationAttempt.eolsAssessmentId,
        userId: parseInt(appState.userId, 10),
        score: 1,
        totalCorrect: 1,
        totalAnswered: 1,
        responseTime: null, // will be populated on BE
        offsetSeconds: 0,
        timeSpent: secondsCompleted
      };

      return submitReviewAttemptAction({
        appState,
        appStateRef,
        setAppState,
        recommendationAttemptId: recommendationAttempt.id,
        assessmentSubmission: submission,
      }).then((response) => {
        return response;
      });
    };

    return (
      <WrappedComponent
        {...props}
        recommendation={recommendation}
        recommendationAttempt={recommendationAttempt}
        recommendationFeedback={state.recommendationFeedback}
        referer={referer}
        handleSubmitAttempt={handleSubmitAttempt}
      />
    );
  };

  const enhance = flowRight(ELSWithToastService);

  return enhance(RecommendationPlayer) as React.FC<ComponentProps>;
};

export default withRecommendationPlayer;
